/**
 * Layers pane — spec §F5.2 (second half), top→bottom:
 *   1. *Filter layers* input (live)
 *   2. a **Raster layer** `<select>` — one drawn raster at a time — and, while one is drawn,
 *      its opacity and metadata under it
 *   3. a **Vector layers** `<select>` that *adds*, with everything added listed under it
 *
 * ## The 2026-08-23 client correction (D2), in this file
 *
 * > *"In the Layers pane the dropdowns select ACTUAL LAYERS: a 'Raster layer' select (one
 * > visible raster at a time — the single pick IS the rule; option None; optgroups by domain;
 * > unavailable rows disabled) and a 'Vector layers' select that ADDS a vector on pick."*
 *
 * The split is the point, and it is a split by **what the reader can have at once** rather
 * than by §D5 `kind`: two opaque tile layers stack, so a raster is a single-valued choice and
 * a listbox is literally that type; vectors combine freely, so their control is an *add*
 * whose result is a list. R4's "one raster at a time" therefore stops being a rule enforced
 * over a list of checkboxes (disable every other raster row, explain why, hope no other writer
 * forgets) and becomes the shape of the control: picking a second raster **replaces** the
 * first (`setActiveRaster`, one write), and *None* is how you draw none.
 *
 * That deletes a whole class of state from the pane — `raster-busy` rows, their tooltip and
 * their toast — and with it the reader-facing question "why is this row dead?" for the one
 * case where nothing was actually wrong. The two states where something *is* wrong keep their
 * place in the list as **disabled options carrying the reason**: §I2 `access: 'none'` says
 * which classification locks it, §D5 `unavailable` says the source is not wired up yet. They
 * stay listed because a layer that vanishes reads as a portal that lost it.
 *
 * Data: `['layers', role]` (role-dependent — see `queryKeys.layers`) and `['config']` for the
 * domain palette and order. Both filters — the free-text box and the R3 domain rail — narrow
 * the **options only**, never the map: a layer that is on stays drawn while it is filtered out
 * of the picker, and stays visible in its own select (`PaneSelect`'s `fallback`), because a
 * control that reads *None* over a drawn layer is a control lying about the map.
 *
 * The §F5.6(d) legend blocks, the permalink and C1's boundary panel are untouched: all three
 * read and write `layersOn`, which is still the one fact about what is drawn.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { fetchConfig, fetchLayers, queryKeys, type Domain, type Layer } from '../../lib/api';
import { DEFAULT_LAYER_OPACITY, ROLE_NAMES, useAppStore } from '../../lib/state';
import { toast } from '../../lib/toast';
import { Chip, type ChipKind } from '../../components/Chip';
import { isRasterKind } from '../map/layerKinds';
import { PaneSelect, type PaneOptGroup } from './PaneSelect';
import { matchesLayerQuery, sourceKindLine } from './layerKind';

export function LayersPane() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const query = useAppStore((s) => s.layerQuery);
  const setLayerQuery = useAppStore((s) => s.setLayerQuery);
  const layersOn = useAppStore((s) => s.layersOn);
  const domFilter = useAppStore((s) => s.domFilter);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });

  /** R3: the rail's pick narrows this pane exactly as it narrows the indicator list. */
  const domains = useMemo(
    () => (config?.domains ?? []).filter((d) => !domFilter || d.id === domFilter),
    [config, domFilter],
  );
  const domainById = useMemo(
    () => new Map((config?.domains ?? []).map((d) => [d.id, d])),
    [config],
  );
  const byId = useMemo(() => new Map((layers ?? []).map((l) => [l.id, l])), [layers]);

  /**
   * The two option lists: `domainId → surviving rows`, in the server's order, split by
   * whether the layer reaches the map as tiles or as geometry.
   *
   * A domain with no surviving row is dropped — an `<optgroup>` heading over nothing is
   * noise, and it is the same rule the Indicators pane applies to an empty tier.
   */
  const { rasters, vectors } = useMemo(() => {
    const nameOf = new Map(domains.map((d) => [d.id, d.name]));
    const pick = (isRaster: boolean): PaneOptGroup[] =>
      domains
        .map((domain) => ({
          label: domain.name,
          options: (layers ?? [])
            .filter(
              (layer) =>
                layer.domain === domain.id &&
                isRasterKind(layer.kind) === isRaster &&
                matchesLayerQuery(layer, query, nameOf.get(layer.domain)),
            )
            .map((layer) => ({
              value: layer.id,
              label: optionLabel(layer, t),
              // §I2 and §D5: offered, dead, and saying which of the two it is. A disabled
              // `<option>` raises no event, so the reason has to be *in* it.
              disabled: layer.access === 'none' || Boolean(layer.unavailable),
              title:
                layer.access === 'none'
                  ? t('layers.lockedTitle')
                  : (layer.note ?? undefined),
            })),
        }))
        .filter((group) => group.options.length > 0);
    return { rasters: pick(true), vectors: pick(false) };
  }, [domains, layers, query, t]);

  /**
   * What is drawn, split the same way the two selects are.
   *
   * Derived from **this pane's own catalogue** rather than from the store's raster register
   * (`selectActiveRaster`): the register is written by whoever holds the map's copy of
   * `['layers', role]`, and this pane must not render "None" over a drawn raster in the
   * moment before — or in a context where — that component has not mounted. Both read the
   * same query, so they agree; the difference is only that this one cannot be early.
   */
  const activeVectors = useMemo(
    () => (layers ?? []).filter((l) => layersOn.has(l.id) && !isRasterKind(l.kind)),
    [layers, layersOn],
  );
  const activeRasterLayer = useMemo(
    () => (layers ?? []).find((l) => layersOn.has(l.id) && isRasterKind(l.kind)) ?? null,
    [layers, layersOn],
  );
  const activeRaster = activeRasterLayer?.id ?? null;
  /**
   * Which ids the raster select is allowed to replace — this pane's own reading of §D5's
   * `kind`, handed to the store so *None* and a swap work whether or not the map's copy of
   * the catalogue has registered them yet (`setActiveRaster`).
   */
  const rasterIds = useMemo(
    () => (layers ?? []).filter((l) => isRasterKind(l.kind)).map((l) => l.id),
    [layers],
  );

  /**
   * §I2/§D5/§I1 in one place, for both selects.
   *
   * The two refusals are unreachable through the control (their options are `disabled`), and
   * they are here anyway: `disabled` is the browser's guarantee for a mouse and a keyboard,
   * not for a programmatic `change`, and this is the only place that knows *why* a row is
   * dead. The third case is not a refusal at all — §I1's LIMITED is a caveat on the way in,
   * said once, because repeating it on every switch-off trains the reader to dismiss the one
   * message that qualifies what they are seeing.
   */
  const admits = (layer: Layer): boolean => {
    if (layer.access === 'none') {
      toast(
        t('layers.lockedToast', {
          name: layer.name,
          classification: t(`layers.class.${layer.classification}`, layer.classification),
          role: t(`role.${role}`, ROLE_NAMES[role]),
        }),
      );
      return false;
    }
    if (layer.unavailable) {
      toast(t('layers.unavailableToast', { name: layer.name, note: layer.note ?? '' }));
      return false;
    }
    if (layer.access === 'limited') toast(t('layers.limitedToast'));
    return true;
  };

  /** D2: the single pick **is** the one-raster rule. `''` is the select's *None* option. */
  const pickRaster = (id: string) => {
    if (!id) {
      setActiveRaster(null, rasterIds);
      return;
    }
    const layer = byId.get(id);
    if (!layer || !admits(layer)) return;
    setActiveRaster(id, rasterIds);
  };

  /** D2: the vector select **adds**, and snaps back to its placeholder (see `value=""`). */
  const addVector = (id: string) => {
    if (!id) return;
    const layer = byId.get(id);
    if (!layer || layersOn.has(id) || !admits(layer)) return;
    toggleLayer(id, true);
  };

  const noMatch = query.trim().length > 0 && !rasters.length && !vectors.length;

  return (
    <div className="sb-pane active" id="pane-layers">
      <div className="sb-section">
        <div className="sb-title">{t('sidebar.filterLayers')}</div>
        <input
          className="std"
          id="layerFilter"
          type="search"
          value={query}
          placeholder={t('sidebar.filterLayersPh')}
          aria-label={t('sidebar.filterLayers')}
          onChange={(e) => setLayerQuery(e.target.value)}
        />
        {noMatch ? (
          <div className="note info" data-nomatch="">
            {t('layers.noMatch', { query })}
          </div>
        ) : null}
      </div>

      <div className="sb-section">
        <div className="sb-title">{t('layers.rasterLayer')}</div>
        <PaneSelect
          id="rasterSelect"
          label={t('layers.rasterLayer')}
          showLabel={false}
          value={activeRaster ?? ''}
          fallback={activeRasterLayer?.name}
          placeholder={t('layers.noRaster')}
          groups={rasters}
          onChange={pickRaster}
          lead={
            <span
              className="dot"
              data-domdot={activeRasterLayer?.domain ?? ''}
              aria-hidden="true"
              style={{
                background: activeRasterLayer
                  ? (domainById.get(activeRasterLayer.domain)?.color ?? 'var(--ink-3)')
                  : 'transparent',
              }}
            />
          }
        />
        {activeRasterLayer ? (
          <ActiveLayer
            layer={activeRasterLayer}
            domain={domainById.get(activeRasterLayer.domain)}
            onOff={() => setActiveRaster(null, rasterIds)}
          />
        ) : null}
      </div>

      <div className="sb-section">
        <div className="sb-title">{t('layers.vectorLayers')}</div>
        <PaneSelect
          id="vectorSelect"
          label={t('layers.vectorLayers')}
          showLabel={false}
          // Never carries a value: it adds, and what it added is the list below.
          value=""
          placeholder={t('layers.addVector')}
          groups={vectors}
          onChange={addVector}
        />
        <div id="vectorActive">
          {activeVectors.map((layer) => (
            <ActiveLayer
              key={layer.id}
              layer={layer}
              domain={domainById.get(layer.domain)}
              onOff={() => toggleLayer(layer.id, false)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** §I2/§D5 in the option text — the chip and the lock icon a row used to carry. */
function optionLabel(layer: Layer, t: TFunction): string {
  if (layer.access === 'none') {
    return t('layers.optLocked', {
      name: layer.name,
      classification: t(`layers.class.${layer.classification}`, {
        defaultValue: layer.classification,
      }),
    });
  }
  if (layer.unavailable) return t('layers.optUnavailable', { name: layer.name });
  return layer.name;
}

export interface ActiveLayerProps {
  layer: Layer;
  domain?: Domain;
  onOff: () => void;
}

/**
 * One drawn layer — the half of §F5.2's row that is an *action on something already chosen*:
 * the live opacity slider, the row's own note, *View metadata →*, and the ✕ that removes it.
 *
 * It is deliberately not a checkbox any more. What is in this list is what is on, so a tick
 * box would be a control that can only ever be un-ticked — and the two ways to switch a layer
 * off (here and §F5.6(d)'s legend ✕) already share one store action, so they cannot disagree.
 */
function ActiveLayer({ layer, domain, onOff }: ActiveLayerProps) {
  const { t } = useTranslation();
  const opacity = useAppStore((s) => s.layerOpacity[layer.id] ?? DEFAULT_LAYER_OPACITY);
  const setLayerOpacity = useAppStore((s) => s.setLayerOpacity);
  const pct = Math.round(opacity * 100);
  const off = t('layers.turnOff', { name: layer.name });

  return (
    <div className="lay-active" data-layer={layer.id}>
      <div className="lay-active-head">
        <span
          className="dot"
          aria-hidden="true"
          style={{ background: domain?.color ?? 'var(--ink-3)' }}
        />
        <span className="lay-name">
          {layer.name}
          <small>{sourceKindLine(layer)}</small>
        </span>
        {layer.is_provisional ? (
          <Chip kind="provisional" title={t('layers.provisionalTitle')}>
            {t('layers.provisional')}
          </Chip>
        ) : null}
        <Chip kind={layer.classification as ChipKind}>
          {t(`layers.class.${layer.classification}`, layer.classification)}
        </Chip>
        <button
          type="button"
          className="lay-off"
          data-layoff={layer.id}
          title={off}
          aria-label={off}
          onClick={onOff}
        >
          ✕
        </button>
      </div>
      <div className="opacity-line">
        <label htmlFor={`layer-op-${layer.id}`}>{t('layers.opacity')}</label>
        <input
          id={`layer-op-${layer.id}`}
          type="range"
          min={10}
          max={100}
          value={pct}
          data-op={layer.id}
          aria-label={t('layers.opacityFor', { name: layer.name })}
          onChange={(e) => setLayerOpacity(layer.id, Number(e.target.value) / 100)}
        />
        <span style={{ fontFamily: 'var(--mono)', width: 32, textAlign: 'right' }}>{pct}%</span>
      </div>
      <div className="layer-desc">
        {layer.note ??
          t('layers.descFallback', {
            source: layer.source_org,
            domain: domain?.name ?? layer.domain,
          })}
      </div>
      <button
        type="button"
        className="layer-meta-btn"
        data-meta={layer.id}
        onClick={() => toast(t('layers.metadataM5'))}
      >
        {t('layers.viewMetadata')}
      </button>
    </div>
  );
}

export default LayersPane;
